import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';
import { VideoStateTool } from './VideoStateTool';

export const setMusicVolumeSchema = z.object({
    volume: z
        .number()
        .min(0)
        .max(100)
        .describe('Volume level from 0 (silent) to 100 (full volume)'),
});
export type SetMusicVolumeArgs = z.infer<typeof setMusicVolumeSchema>;

export class SetMusicVolumeTool extends VideoStateTool<typeof setMusicVolumeSchema> {
    protected override readonly schema = setMusicVolumeSchema;
    protected override readonly toolName = TOOL_NAMES.setMusicVolume;
    public override readonly subagentAccess = SubagentAccess.BLOCKED;
    protected override readonly toolDescription =
        'Set the music volume for the video. Choose it from the mix rather than a fixed number: with narration, keep the music clearly under the voice (roughly 10-15 for busy, energetic tracks; 15-25 for sparse, ambient ones); with no narration the music is the soundtrack, so set it much louder (roughly 50-80).';

    public override getToolDisplayName(): string {
        return 'Setting music volume';
    }

    public get statusIcon(): string {
        return 'volume_up';
    }

    public async execute(args: SetMusicVolumeArgs, context: ToolExecutionContext) {
        const { volume } = args;

        if (!this._luminary.musicAsset) {
            return `Error: No music is currently applied to the video. Please set music first using the ${TOOL_NAMES.setMusic} tool.`;
        }

        if (this._luminary.musicAsset.volume === volume) {
            return `No change: the music volume is already ${volume}%.`;
        }

        try {
            this._luminary.musicAsset.volume = volume;
            this.checkpointUnderTurn(context.parentActionGroup);
            return `Successfully set global music volume to ${volume}%.`;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting music volume: ${errorMessage}`;
        }
    }
}
